"use client";

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Search, ChevronLeft, ChevronRight } from "lucide-react";
import { BlogCard, type CardPost } from "./BlogCard";

const PAGE_SIZE = 9;

type Labels = {
  searchPlaceholder: string;
  searchAria: string;
  noResults: string;
  prevAria: string;
  nextAria: string;
  pageAria: string;
};

/** Rivela il figlio con animazione quando entra in viewport. */
function Reveal({ index, children }: { index: number; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true);
          io.disconnect();
        }
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.12 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`blog-reveal ${shown ? "is-visible" : ""}`}
      style={{ transitionDelay: `${(index % 3) * 90}ms` }}
    >
      {children}
    </div>
  );
}

/** Cerchio pagina, stile "glass" delle icone social; ciano quando attivo. */
function PageDot({
  active,
  onClick,
  ariaLabel,
  children,
  disabled,
}: {
  active?: boolean;
  onClick: () => void;
  ariaLabel: string;
  children: ReactNode;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-current={active ? "page" : undefined}
      className={`flex h-10 w-10 items-center justify-center rounded-full border text-sm font-medium transition-all duration-300 disabled:pointer-events-none disabled:opacity-35 ${
        active
          ? "border-[#2FD9C5]/50 bg-[#2FD9C5]/15 text-[#2FD9C5] shadow-[0_0_20px_rgba(47,217,197,0.35),inset_0_1px_0_rgba(255,255,255,0.14)]"
          : "border-white/10 bg-white/[0.06] text-foreground/80 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] supports-[backdrop-filter]:backdrop-blur-md hover:border-[#2FD9C5]/50 hover:bg-[#2FD9C5]/10 hover:text-[#2FD9C5]"
      }`}
    >
      {children}
    </button>
  );
}

export function BlogGrid({
  posts,
  blogBase,
  locale,
  minLabel,
  readLabel,
  labels,
}: {
  posts: CardPost[];
  blogBase: string;
  locale: "it" | "en";
  minLabel: string;
  readLabel: string;
  labels: Labels;
}) {
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const topRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return posts;
    return posts.filter((p) =>
      [p.title, p.description, p.category, ...p.tags]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [posts, query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, totalPages - 1);
  const paged = filtered.slice(current * PAGE_SIZE, current * PAGE_SIZE + PAGE_SIZE);

  // Nuova ricerca -> torna a pagina 1.
  useEffect(() => {
    setPage(0);
  }, [query]);

  const goTo = (p: number) => {
    setPage(p);
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div ref={topRef}>
      {/* Ricerca articoli (ancorata a sinistra, coerente con hero e griglia) */}
      <div className="relative mb-10 max-w-xl">
        <Search className="pointer-events-none absolute left-4 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={labels.searchPlaceholder}
          aria-label={labels.searchAria}
          className="h-12 w-full rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] pl-11 pr-4 text-[15px] text-foreground placeholder:text-muted-foreground/70 transition-colors focus:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/25"
        />
      </div>

      {filtered.length === 0 ? (
        <p className="py-16 text-center text-muted-foreground">{labels.noResults}</p>
      ) : (
        <>
          <div key={`${query}-${current}`} className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
            {paged.map((post, i) => (
              <Reveal key={post.slug} index={i}>
                <BlogCard
                  post={post}
                  href={`${blogBase}/${post.slug}`}
                  locale={locale}
                  minLabel={minLabel}
                  readLabel={readLabel}
                />
              </Reveal>
            ))}
          </div>

          {totalPages > 1 && (
            <nav className="mt-12 flex items-center justify-center gap-2.5" aria-label="Pagination">
              <PageDot
                onClick={() => goTo(current - 1)}
                disabled={current === 0}
                ariaLabel={labels.prevAria}
              >
                <ChevronLeft className="h-[18px] w-[18px]" />
              </PageDot>
              {Array.from({ length: totalPages }, (_, i) => (
                <PageDot
                  key={i}
                  active={i === current}
                  onClick={() => goTo(i)}
                  ariaLabel={`${labels.pageAria} ${i + 1}`}
                >
                  {i + 1}
                </PageDot>
              ))}
              <PageDot
                onClick={() => goTo(current + 1)}
                disabled={current === totalPages - 1}
                ariaLabel={labels.nextAria}
              >
                <ChevronRight className="h-[18px] w-[18px]" />
              </PageDot>
            </nav>
          )}
        </>
      )}
    </div>
  );
}
